import { Copy, Phone } from 'lucide-react';
import { useState } from 'react';

interface ContactRowProps {
  label: string;
  value: string | null | undefined;
  /** When true, render a tel: link + small "Call" button. */
  callable?: boolean;
}

export function ContactRow({ label, value, callable }: ContactRowProps) {
  const [copied, setCopied] = useState(false);

  if (!value) {
    return (
      <div className="flex items-center justify-between py-1 text-sm">
        <span className="text-gray-500">{label}</span>
        <span className="text-gray-600 italic">Not set</span>
      </div>
    );
  }

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: select text in a temporary input
      const input = document.createElement('input');
      input.value = value;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-gray-500">{label}</span>
      <div className="flex items-center gap-2">
        {callable ? (
          <a
            href={`tel:${value.replace(/\s+/g, '')}`}
            className="font-mono text-gray-200 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </a>
        ) : (
          <span className="font-mono text-gray-200">{value}</span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onCopy(); }}
          className="rounded p-1 text-gray-500 hover:bg-gray-800 hover:text-gray-300 transition-colors"
          aria-label={`Copy ${label}`}
          title={copied ? 'Copied!' : `Copy ${label}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
        {callable ? (
          <a
            href={`tel:${value.replace(/\s+/g, '')}`}
            className="rounded bg-emerald-500/10 p-1 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
            aria-label={`Call ${value}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Phone className="h-3.5 w-3.5" />
          </a>
        ) : null}
      </div>
    </div>
  );
}
